import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { from, lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserDto } from '../../pages/individual-license/interfaces/individual-license.interface';
import { RoleService } from '../role/role.service';

/** Paginated response for GetUsers query. */
interface GetUsersResponse {
  GetUsers: {
    nodes: Array<UserDto>;
    pageInfo: {
      hasNextPage: boolean;
      endCursor?: string;
    };
  };
}

/** Response for AssignLicenseToUser mutation. */
interface AssignLicenseResponse {
  AssignLicenseToUser: boolean;
}

/** Response for RevokeLicenseFromUser mutation. */
interface RevokeLicenseResponse {
  RevokeLicenseFromUser: boolean;
}

const GET_USERS_WITH_LICENSES: DocumentNode = gql`
  query GetUsers($first: Int, $after: String, $accessLevel: DataAccessLevel) {
    GetUsers(first: $first, after: $after, accessLevel: $accessLevel) {
      nodes {
        id
        userName
        email
        displayName
        organization
        primaryUserTenant {
          tenant {
            id
            name
          }
        }
        createdOnUtc
        licenses {
          id
          isActive
          plan {
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const ASSIGN_LICENSE: DocumentNode = gql`
  mutation AssignLicenseToUser($userId: UUID!, $licenseId: UUID!, $accessLevel: DataAccessLevel) {
    AssignLicenseToUser(userId: $userId, licenseId: $licenseId, accessLevel: $accessLevel)
  }
`;

const REVOKE_LICENSE: DocumentNode = gql`
  mutation RevokeLicenseFromUser($userId: UUID!, $licenseId: UUID!, $accessLevel: DataAccessLevel) {
    RevokeLicenseFromUser(userId: $userId, licenseId: $licenseId, accessLevel: $accessLevel)
  }
`;

/** Service for managing individual user licenses and assignments. */
@Injectable()
export class IndividualLicenseService {
  /** Apollo client for GraphQL queries and mutations. */
  private readonly apollo: Apollo = inject(Apollo);

  /** Role service for determining data access level. */
  private readonly roleService: RoleService = inject(RoleService);

  /**
   * Get all users with their license assignments.
   * Auto-paginates through all pages to return every user.
   * @returns Observable<Array<UserDto>> - Array of all users with nested licenses
   */
  public getUsersWithLicenses(): Observable<Array<UserDto>> {
    return from(this.fetchAllUsers());
  }

  /**
   * Fetches all users by paginating through the GraphQL cursor-based API.
   * @param pageSize - Number of records per page
   * @returns Promise<Array<UserDto>> - All users across all pages
   */
  private async fetchAllUsers(pageSize: number = 50): Promise<Array<UserDto>> {
    const allUsers: Array<UserDto> = [];
    let hasNextPage: boolean = true;
    let after: string | undefined;

    while (hasNextPage) {
      const result: GetUsersResponse = await lastValueFrom(
        this.apollo
          .query<GetUsersResponse>({
            query: GET_USERS_WITH_LICENSES,
            variables: { first: pageSize, after, accessLevel: this.roleService.getAccessLevel() },
            fetchPolicy: 'network-only'
          })
          .pipe(map((response): GetUsersResponse => response.data as GetUsersResponse))
      );

      const nodes: Array<UserDto> = result?.GetUsers?.nodes ?? [];
      allUsers.push(...nodes);

      hasNextPage = result?.GetUsers?.pageInfo?.hasNextPage ?? false;
      after = result?.GetUsers?.pageInfo?.endCursor;
    }

    return allUsers;
  }

  /**
   * Assign a single license to a user.
   * @param userId - The user ID
   * @param licenseId - The license ID to assign
   * @returns Observable<boolean> - Success status
   */
  public assignLicenseToUser(userId: string, licenseId: string): Observable<boolean> {
    return this.apollo
      .mutate<AssignLicenseResponse>({
        mutation: ASSIGN_LICENSE,
        variables: { userId, licenseId, accessLevel: this.roleService.getAccessLevel() }
      })
      .pipe(map((result): boolean => result.data?.AssignLicenseToUser ?? false));
  }

  /**
   * Revoke a license from a user.
   * @param userId - The user ID
   * @param licenseId - The license ID to revoke
   * @returns Observable<boolean> - Success status
   */
  public revokeLicenseFromUser(userId: string, licenseId: string): Observable<boolean> {
    return this.apollo
      .mutate<RevokeLicenseResponse>({
        mutation: REVOKE_LICENSE,
        variables: { userId, licenseId, accessLevel: this.roleService.getAccessLevel() }
      })
      .pipe(map((result): boolean => result.data?.RevokeLicenseFromUser ?? false));
  }
}
